import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Build,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Star,
  PlaylistAdd,
  Waves,
} from '@mui/icons-material';
import { UserEquipment, EQUIPMENT_TYPE_LABELS } from '../types/Equipment';
import { equipmentService } from '../services/equipmentService';
import { preferencesManager } from '../utils/preferencesManager';
import ServiceDialog from './ServiceDialog';
import EquipmentDialog from './EquipmentDialog';
import BulkAssignDivesDialog from './BulkAssignDivesDialog';
import EquipmentDivesDialog from './EquipmentDivesDialog';
import AppNavigation from './AppNavigation';
import EquipmentTypeIcon from './EquipmentTypeIcon';
import BuoyancyCalculator from './BuoyancyCalculator';
import SignInNudge from './SignInNudge';
import { authService } from '../services/authService';

const EquipmentManagement: React.FC = () => {
  const [user, setUser] = useState(authService.getUser());
  const [equipment, setEquipment] = useState<UserEquipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<UserEquipment | null>(null);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [equipmentDialogOpen, setEquipmentDialogOpen] = useState(false);
  const [bulkAssignDialogOpen, setBulkAssignDialogOpen] = useState(false);
  const [equipmentDivesDialogOpen, setEquipmentDivesDialogOpen] = useState(false);
  const [selectedForBulkAssign, setSelectedForBulkAssign] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadEquipment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadEquipment = async () => {
    const userId = preferencesManager.getUserId() ?? user?.userId;
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create a timeout promise
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 15000)
      );

      // Race between the API call and timeout
      const data = await Promise.race([
        equipmentService.getUserEquipment(userId),
        timeoutPromise
      ]);

      setEquipment(data.filter(e => e.isActive));
    } catch (error: any) {
      console.error('Failed to load equipment:', error);

      if (error.message === 'Request timeout') {
        setError('Loading equipment timed out. Please refresh the page or try again later.');
      } else if (error.message?.includes('authentication') || error.message?.includes('Unauthorized')) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError('Failed to load equipment. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, eq: UserEquipment) => {
    setAnchorEl(event.currentTarget);
    setSelectedEquipment(eq);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleRecordService = () => {
    handleMenuClose();
    setServiceDialogOpen(true);
  };

  const handleServiceRecorded = (updatedEquipment: UserEquipment) => {
    setEquipment(prev =>
      prev.map(eq =>
        eq.equipmentId === updatedEquipment.equipmentId ? updatedEquipment : eq
      )
    );
  };

  const handleEdit = () => {
    handleMenuClose();
    setEquipmentDialogOpen(true);
  };

  const handleAddEquipment = () => {
    setSelectedEquipment(null);
    setEquipmentDialogOpen(true);
  };

  const handleEquipmentSaved = (savedEquipment: UserEquipment) => {
    if (selectedEquipment) {
      // Update existing equipment
      setEquipment(prev =>
        prev.map(eq =>
          eq.equipmentId === savedEquipment.equipmentId ? savedEquipment : eq
        )
      );
    } else {
      // Add new equipment
      setEquipment(prev => [...prev, savedEquipment]);
    }
  };

  const handleToggleBulkSelect = (equipmentId: string) => {
    setSelectedForBulkAssign(prev => {
      const newSet = new Set(prev);
      if (newSet.has(equipmentId)) {
        newSet.delete(equipmentId);
      } else {
        newSet.add(equipmentId);
      }
      return newSet;
    });
  };

  const handleOpenBulkAssign = () => {
    if (selectedForBulkAssign.size === 0) {
      alert('Please select at least one piece of equipment');
      return;
    }
    setBulkAssignDialogOpen(true);
  };

  const handleBulkAssignComplete = () => {
    setSelectedForBulkAssign(new Set());
    setBulkAssignDialogOpen(false);
    alert('Equipment successfully assigned to selected dives!');
  };

  const handleViewDives = () => {
    handleMenuClose();
    setEquipmentDivesDialogOpen(true);
  };

  const handleEquipmentDivesSaved = () => {
    setEquipmentDivesDialogOpen(false);
    loadEquipment(); // Reload equipment to update dive counts
  };

  const handleDelete = async () => {
    if (!selectedEquipment) return;

    const userId = preferencesManager.getUserId();
    if (!userId) return;

    if (window.confirm(`Are you sure you want to delete ${selectedEquipment.brand || selectedEquipment.equipmentType}?`)) {
      try {
        await equipmentService.deleteEquipment(userId, selectedEquipment.equipmentId);
        setEquipment(prev => prev.filter(eq => eq.equipmentId !== selectedEquipment.equipmentId));
        handleMenuClose();
      } catch (error) {
        console.error('Failed to delete equipment:', error);
        alert('Failed to delete equipment');
      }
    }
  };

  const getServiceStatus = (eq: UserEquipment) => {
    // If equipment is not serviceable, return not applicable status
    if (!eq.isServiceable) {
      return { status: 'not_serviceable', message: 'Not serviceable', color: '#9e9e9e', progress: 0 };
    }

    if (!eq.serviceIntervalMonths || eq.serviceIntervalMonths === 0) {
      return { status: 'none', message: 'No service interval set', color: '#9e9e9e', progress: 0 };
    }

    // Calculate months since last service or purchase
    const referenceDate = eq.lastServiceDate || eq.purchaseDate;
    if (!referenceDate) {
      return { status: 'none', message: 'No service date set', color: '#9e9e9e', progress: 0 };
    }

    const refDate = new Date(referenceDate);
    const now = new Date();
    const monthsSinceService = (now.getFullYear() - refDate.getFullYear()) * 12 + (now.getMonth() - refDate.getMonth());
    const progress = (monthsSinceService / eq.serviceIntervalMonths) * 100;

    const intervalYears = eq.serviceIntervalMonths === 12 ? '1 year' :
                          eq.serviceIntervalMonths === 24 ? '2 years' :
                          `${eq.serviceIntervalMonths} months`;

    if (progress < 70) {
      return {
        status: 'good',
        message: `${monthsSinceService} months of ${intervalYears}`,
        color: '#4caf50',
        progress,
      };
    } else if (progress < 90) {
      return {
        status: 'warning',
        message: `${monthsSinceService} months of ${intervalYears} - Service soon`,
        color: '#ff9800',
        progress,
      };
    } else {
      return {
        status: 'critical',
        message: `${monthsSinceService} months of ${intervalYears} - Service due!`,
        color: '#f44336',
        progress,
      };
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'good':
        return <CheckCircle sx={{ fontSize: 20, color: '#4caf50' }} />;
      case 'warning':
        return <Warning sx={{ fontSize: 20, color: '#ff9800' }} />;
      case 'critical':
        return <ErrorIcon sx={{ fontSize: 20, color: '#f44336' }} />;
      default:
        return null;
    }
  };

  if (!user) {
    return (
      <Box>
        <AppNavigation />
        <SignInNudge
          feature="track your equipment"
          description="Keep your gear list, service history, and dive assignments in one place — sign in or create a free account to get started."
          onSignedIn={() => setUser(authService.getUser())}
        />
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadEquipment}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      {/* Unified Navigation */}
      <AppNavigation />

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Equipment Management
        </Typography>
        <Box display="flex" gap={2}>
          {selectedForBulkAssign.size > 0 ? (
            <>
              <Button
                variant="outlined"
                onClick={() => setSelectedForBulkAssign(new Set())}
              >
                Cancel ({selectedForBulkAssign.size} selected)
              </Button>
              <Button
                variant="contained"
                startIcon={<PlaylistAdd />}
                onClick={handleOpenBulkAssign}
              >
                Assign to Dives
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddEquipment}
            >
              Add Equipment
            </Button>
          )}
        </Box>
      </Box>

      {/* Info Alert */}
      {equipment.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            No equipment found. Add your dive gear to track usage, maintenance, and service intervals.
          </Typography>
        </Alert>
      )}

      {/* Bulk Assignment Help */}
      {selectedForBulkAssign.size === 0 && equipment.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> Click on an equipment card to start bulk assignment mode, then select multiple pieces of equipment to assign them to your dives all at once.
          </Typography>
        </Alert>
      )}

      {/* Equipment Grid */}
      <Grid container spacing={3}>
        {equipment.map((eq) => {
          const serviceStatus = getServiceStatus(eq);

          const isSelectedForBulkAssign = selectedForBulkAssign.has(eq.equipmentId);

          return (
            <Grid item xs={12} sm={6} md={4} key={eq.equipmentId}>
              <Card
                elevation={2}
                sx={{
                  border: isSelectedForBulkAssign ? '2px solid' :
                         serviceStatus.status === 'critical' ? '2px solid' :
                         serviceStatus.status === 'warning' ? '2px solid' : 'none',
                  borderColor: isSelectedForBulkAssign ? 'primary.main' :
                              serviceStatus.status === 'critical' ? '#f44336' :
                              serviceStatus.status === 'warning' ? '#ff9800' : 'transparent',
                  cursor: 'pointer',
                  backgroundColor: serviceStatus.status === 'critical' ? 'rgba(244, 67, 54, 0.05)' :
                                  serviceStatus.status === 'warning' ? 'rgba(255, 152, 0, 0.05)' : 'inherit',
                  '&:hover': {
                    backgroundColor: serviceStatus.status === 'critical' ? 'rgba(244, 67, 54, 0.1)' :
                                    serviceStatus.status === 'warning' ? 'rgba(255, 152, 0, 0.1)' : 'action.hover',
                  },
                }}
                onClick={() => handleToggleBulkSelect(eq.equipmentId)}
              >
                <CardContent>
                  {/* Header with Menu */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {/* Show checkbox when in bulk assign mode */}
                      {(selectedForBulkAssign.size > 0 || isSelectedForBulkAssign) && (
                        <Checkbox
                          checked={isSelectedForBulkAssign}
                          onChange={() => handleToggleBulkSelect(eq.equipmentId)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <EquipmentTypeIcon type={eq.equipmentType} size={34} />
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                            {EQUIPMENT_TYPE_LABELS[eq.equipmentType as keyof typeof EQUIPMENT_TYPE_LABELS]}
                          </Typography>
                          {eq.isDefault && (
                            <Tooltip title="Default equipment - automatically attached to new dives">
                              <Star sx={{ fontSize: 18, color: 'warning.main' }} />
                            </Tooltip>
                          )}
                        </Box>
                        {(eq.brand || eq.model) && (
                          <Typography variant="body2" color="textSecondary">
                            {[eq.brand, eq.model].filter(Boolean).join(' ')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, eq)}
                    >
                      <MoreVert />
                    </IconButton>
                  </Box>

                  {/* Equipment Details */}
                  <Box sx={{ mb: 2 }}>
                    {eq.serialNumber && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        S/N: {eq.serialNumber}
                      </Typography>
                    )}
                  </Box>

                  {/* Dive Statistics */}
                  <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Dive Statistics
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Dives: <strong>{eq.diveCount}</strong>
                    </Typography>
                    {eq.isServiceable ? (
                      <>
                        <Typography variant="body2" color="textSecondary">
                          Dives Since Service: <strong>{eq.diveCount - (eq.divesAtLastService || 0)}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Last Serviced: <strong>
                            {eq.lastServiceDate
                              ? new Date(eq.lastServiceDate).toLocaleDateString()
                              : eq.purchaseDate
                                ? `Never (Purchased ${new Date(eq.purchaseDate).toLocaleDateString()})`
                                : 'Never'}
                          </strong>
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#9e9e9e', fontStyle: 'italic', mt: 0.5 }}>
                        Not serviceable
                      </Typography>
                    )}
                  </Box>

                  {/* Service Status - Only show if serviceable and not 'none' */}
                  {eq.isServiceable && serviceStatus.status !== 'none' && serviceStatus.status !== 'not_serviceable' && (
                    <Box>
                      <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                        {getStatusIcon(serviceStatus.status)}
                        <Typography variant="caption" sx={{ color: serviceStatus.color, fontWeight: 'bold' }}>
                          {serviceStatus.message}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(serviceStatus.progress, 100)}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: 'action.hover',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: serviceStatus.color,
                          },
                        }}
                      />
                      {eq.lastServiceDate && (
                        <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
                          Last serviced: {new Date(eq.lastServiceDate).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Notes */}
                  {eq.notes && (
                    <Box sx={{ mt: 2, p: 1, backgroundColor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" color="textSecondary">
                        {eq.notes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'flex-start', px: 2, pb: 2 }}>
                  {eq.isServiceable && (
                    <Button
                      size="small"
                      startIcon={<Build />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEquipment(eq);
                        setServiceDialogOpen(true);
                      }}
                    >
                      Record Service
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 4 }}>
        <BuoyancyCalculator />
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewDives}>
          <Waves fontSize="small" sx={{ mr: 1 }} />
          View Attached Dives
        </MenuItem>
        {selectedEquipment?.isServiceable && (
          <MenuItem onClick={handleRecordService}>
            <Build fontSize="small" sx={{ mr: 1 }} />
            Record Service
          </MenuItem>
        )}
        <MenuItem onClick={handleEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Service Dialog */}
      <ServiceDialog
        open={serviceDialogOpen}
        equipment={selectedEquipment}
        onClose={() => setServiceDialogOpen(false)}
        onServiceRecorded={handleServiceRecorded}
      />

      {/* Equipment Dialog */}
      <EquipmentDialog
        open={equipmentDialogOpen}
        equipment={selectedEquipment}
        onClose={() => {
          setEquipmentDialogOpen(false);
          setSelectedEquipment(null);
        }}
        onSaved={handleEquipmentSaved}
      />

      {/* Bulk Assign Dives Dialog */}
      <BulkAssignDivesDialog
        open={bulkAssignDialogOpen}
        selectedEquipment={equipment.filter(eq => selectedForBulkAssign.has(eq.equipmentId))}
        onClose={() => setBulkAssignDialogOpen(false)}
        onAssigned={handleBulkAssignComplete}
      />

      {/* Equipment Dives Dialog - View and edit dives for a single equipment */}
      <EquipmentDivesDialog
        open={equipmentDivesDialogOpen}
        equipment={selectedEquipment}
        onClose={() => setEquipmentDivesDialogOpen(false)}
        onSaved={handleEquipmentDivesSaved}
      />
    </Box>
  );
};

export default EquipmentManagement;
